import React, { useState, useRef } from 'react';
import { Upload, Type, X } from 'lucide-react';
import { supabase } from '../../../supabaseClient';
import { useToast } from '../../../contexts/ToastContext';
import { useAvatar } from '../../../contexts/AvatarContext';
import Avatar from './Avatar';

const AvatarUpload = ({ 
  currentAvatar, 
  onClose,
  userId 
}) => {
  const { updateAvatar } = useAvatar();
  const [avatarType, setAvatarType] = useState(currentAvatar?.type || 'initials');
  const [avatarColor, setAvatarColor] = useState(currentAvatar?.color || '#6366f1');
  const [avatarInitials, setAvatarInitials] = useState(currentAvatar?.initials || '');
  const [avatarUrl, setAvatarUrl] = useState(currentAvatar?.url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const addToast = useToast();

  const predefinedColors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b',
    '#10b981', '#06b6d4', '#3b82f6', '#6b7280', '#1f2937'
  ];

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addToast('Please select a valid image file', 'danger');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      addToast('File size must be less than 5MB', 'danger');
      return;
    }

    setUploading(true);
    
    try {
      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      
      const { error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      setAvatarUrl(publicUrl);
      setAvatarType('image');
      addToast('Avatar uploaded successfully!', 'success');
      
    } catch (error) {
      console.error('Error uploading avatar:', error);
      addToast('Error uploading avatar. Please try again.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleInitialsChange = (e) => {
    const value = e.target.value.toUpperCase();
    // Only allow A-Z, 0-9, dot, dash, up to 3 characters
    const filtered = value.replace(/[^A-Z0-9.-]/g, '').slice(0, 3);
    setAvatarInitials(filtered);
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const avatarData = {
        type: avatarType,
        color: avatarColor,
        initials: avatarInitials,
        url: avatarUrl
      };

      // Use the context's updateAvatar method
      const result = await updateAvatar(avatarData);
      
      if (result.success) {
        addToast('Avatar updated successfully!', 'success');
        onClose();
      } else {
        throw new Error(result.error || 'Failed to update avatar');
      }
      
    } catch (error) {
      console.error('Error saving avatar:', error);
      addToast('Error saving avatar. Please try again.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const previewAvatar = {
    type: avatarType,
    color: avatarColor,
    initials: avatarInitials,
    url: avatarUrl
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Avatar
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Avatar Preview */}
        <div className="text-center mb-6">
          <div className="inline-block">
            <Avatar
              size="2xl"
              src={previewAvatar.url}
              initials={previewAvatar.initials}
              color={previewAvatar.color}
              type={previewAvatar.type}
            />
          </div>
        </div>

        {/* Avatar Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Avatar Type
          </label>
          <div className="flex space-x-4">
            <button
              onClick={() => setAvatarType('initials')}
              className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                avatarType === 'initials'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
              }`}
            >
              <Type className="w-4 h-4 mr-2" />
              Initials
            </button>
            <button
              onClick={() => setAvatarType('image')}
              className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${
                avatarType === 'image'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
              }`}
            >
              <Upload className="w-4 h-4 mr-2" />
              Image
            </button>
          </div>
        </div>

        {/* Initials Configuration */}
        {avatarType === 'initials' && (
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Initials (A-Z, 0-9, dot, dash - max 3 chars)
              </label>
              <input
                type="text"
                value={avatarInitials}
                onChange={handleInitialsChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="ABC"
                maxLength={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Background Color
              </label>
              <div className="flex flex-wrap gap-2">
                {predefinedColors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setAvatarColor(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      avatarColor === color
                        ? 'border-gray-900 dark:border-white scale-110'
                        : 'border-gray-300 dark:border-gray-600 hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <input
                type="color"
                value={avatarColor}
                onChange={(e) => setAvatarColor(e.target.value)}
                className="mt-2 w-full h-10 rounded-lg border border-gray-300 dark:border-gray-600"
              />
            </div>
          </div>
        )}

        {/* Image Upload */}
        {avatarType === 'image' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload Image
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Choose Image'}
            </button>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Max file size: 5MB. Supports JPG, PNG, GIF, WebP.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarUpload;
